// Stat strip — top-left chip: armour / shells / mines / trees as label +
// horizontal mini-bar + number, plus a meta row with kills, deaths, and the
// gun-range readout (sightLen, shown in tiles — sightLen is in HALF-tiles,
// Cheshire Brain.h MAXRANGE). Plain DOM, no sprites: at phone-arm's-length
// the 12px letter labels + colour-coded bars read faster than 12px icon art.
//
// Repaints are gated on the snapshot version (tank stats only change when a
// snapshot lands), mirroring the desktop RightColumn.

import {
  TANK_FULL_ARMOUR,
  TANK_FULL_MINES,
  TANK_FULL_SHELLS,
  TANK_FULL_TREES,
} from '@bolo2k/shared';
import type { ClientTank } from '@bolo2k/client-core';

export interface StatStrip {
  readonly el: HTMLElement;
  /** Repaint from the local tank (null = dead/spectating/not welcomed). */
  update(me: ClientTank | null, stateVersion: number): void;
}

interface BarRefs {
  fill: HTMLElement;
  val: HTMLElement;
  max: number;
}

export function createStatStrip(): StatStrip {
  const el = document.createElement('div');
  el.className = 'm3k-chip m3k-stats';

  // Mission-ordered rows: armour first (most vital at a glance on mobile).
  const armour = makeRow(el, 'AR', 'armour');
  const shells = makeRow(el, 'SH', 'shells');
  const mines = makeRow(el, 'MN', 'mines');
  const trees = makeRow(el, 'TR', 'trees');

  const meta = document.createElement('div');
  meta.className = 'm3k-stat-meta';
  const kills = metaCell(meta, 'K');
  const deaths = metaCell(meta, 'D');
  const range = metaCell(meta, 'RNG');
  el.appendChild(meta);

  let lastVersion = -2; // sentinel: repaint on first call either way
  let lastHadTank = false;

  function update(me: ClientTank | null, stateVersion: number): void {
    const hasTank = me !== null && me.alive;
    if (stateVersion === lastVersion && hasTank === lastHadTank) return;
    lastVersion = stateVersion;
    lastHadTank = hasTank;

    el.classList.toggle('m3k-stats--dead', !hasTank);
    if (!me) {
      setBar(armour, 0, TANK_FULL_ARMOUR);
      setBar(shells, 0, TANK_FULL_SHELLS);
      setBar(mines, 0, TANK_FULL_MINES);
      setBar(trees, 0, TANK_FULL_TREES);
      setText(armour.val, '—');
      setText(shells.val, '—');
      setText(mines.val, '—');
      setText(trees.val, '—');
      setText(kills, '—');
      setText(deaths, '—');
      setText(range, '—');
      return;
    }

    setBar(armour, me.armour, TANK_FULL_ARMOUR);
    setBar(shells, me.shells, TANK_FULL_SHELLS);
    setBar(mines, me.mines, TANK_FULL_MINES);
    setBar(trees, me.trees, TANK_FULL_TREES);
    setText(armour.val, String(me.armour));
    setText(shells.val, String(me.shells));
    setText(mines.val, String(me.mines));
    setText(trees.val, String(me.trees));
    // Armour fill shifts green→amber→red so damage reads without numbers.
    const frac = me.armour / TANK_FULL_ARMOUR;
    armour.fill.style.background = frac > 0.5 ? '#5fd35f' : frac > 0.25 ? '#e0b23a' : '#e05050';
    setText(kills, String(me.kills));
    setText(deaths, String(me.deaths));
    // sightLen is in half-tiles (Cheshire's MAXRANGE=14, GUNSIGHT_MIN=2) —
    // show tiles with one decimal, e.g. 14 → "7.0t".
    setText(range, `${(me.sightLen / 2).toFixed(1)}t`);
  }

  return { el, update };
}

function makeRow(parent: HTMLElement, label: string, kind: string): BarRefs {
  const row = document.createElement('div');
  row.className = 'm3k-stat-row';
  const lab = document.createElement('span');
  lab.className = 'm3k-stat-label';
  lab.textContent = label;
  const bar = document.createElement('div');
  bar.className = 'm3k-stat-bar';
  const fill = document.createElement('div');
  fill.className = `m3k-stat-fill m3k-stat-fill--${kind}`;
  bar.appendChild(fill);
  const val = document.createElement('span');
  val.className = 'm3k-stat-val';
  val.textContent = '—';
  row.append(lab, bar, val);
  parent.appendChild(row);
  return { fill, val, max: 1 };
}

function metaCell(parent: HTMLElement, label: string): HTMLElement {
  const cell = document.createElement('span');
  const b = document.createElement('b');
  b.textContent = '—';
  cell.append(`${label} `, b);
  parent.appendChild(cell);
  return b;
}

function setBar(b: BarRefs, value: number, max: number): void {
  const pct = `${(Math.max(0, Math.min(1, value / max)) * 100).toFixed(1)}%`;
  if (b.fill.style.width !== pct) b.fill.style.width = pct;
}

function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}
